/**
 * Admin & Settings — Phase 15 shell.
 *
 * The existing `Settings.js` page already covers Company Details, Branding,
 * Payment Gateway, and Team Members — all four sub-tabs the spec describes
 * (renamed: Agency Profile ↔ Company, Agents & Users ↔ Team Members,
 * Invoice Settings live alongside Company fields, Notifications is new).
 *
 * Rather than duplicate that 1000-line page, we render it here and add a
 * dedicated Notifications panel on top. The sidebar "Admin & Settings"
 * link routes here, so deep features stay one click away while the new
 * Notifications surface gets a permanent home.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Bell, Wrench, ChevronDown, ChevronRight, Loader2, Database, CheckCircle2, Activity, RefreshCw, AlertTriangle, Archive } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Settings from './Settings';
import PendingAgents from '@/components/admin/PendingAgents';
import PiiAccessLog from '@/components/admin/PiiAccessLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Each toggle persists into the agency settings doc under
// `notifications.<key>`. Defaults are conservative — only the
// payment-received toast is on by default since it's universally wanted.
const NOTIF_TOGGLES = [
  { key: 'emailOnPaymentReceived', label: 'Email when a payment is received', default: true },
  { key: 'emailOnInvoiceOverdue', label: 'Email when an invoice goes overdue', default: true },
  { key: 'whatsappDepartureAlert', label: 'WhatsApp alert when departure is in 48h and a voucher is missing', default: true },
  { key: 'dailyDigestEmail', label: 'Daily digest email (follow-ups, departures, collections)', default: false },
];

const AdminNotifications = () => {
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`, { withCredentials: true })
      .then((res) => {
        const n = (res.data && res.data.notifications) || {};
        const merged = {};
        NOTIF_TOGGLES.forEach((t) => {
          merged[t.key] = n[t.key] === undefined ? t.default : !!n[t.key];
        });
        setPrefs(merged);
      })
      .catch(() => toast.error('Failed to load notification preferences'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    try {
      // Dedicated endpoint accepts arbitrary keys so the strict
      // CompanySettings Pydantic model isn't re-validated here.
      await axios.put(`${BACKEND_URL}/api/settings/notifications`, prefs, { withCredentials: true });
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card className="p-6 mb-6" data-testid="admin-notifications-loading">Loading notification preferences…</Card>;
  }

  return (
    <Card className="p-6 mb-6 border-slate-200" data-testid="admin-notifications">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
      </div>
      <ul className="divide-y divide-slate-100">
        {NOTIF_TOGGLES.map((t) => (
          <NotifRow key={t.key} t={t} prefs={prefs} onToggle={toggle} />
        ))}
      </ul>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-notifications-btn">
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </Card>
  );
};

const NotifRow = ({ t, prefs, onToggle }) => (
  <li className="flex items-center justify-between py-3" data-testid={`notif-${t.key}`}>
    <span className="text-sm text-slate-700">{t.label}</span>
    <Switch checked={!!prefs[t.key]} onCheckedChange={() => onToggle(t.key)} data-testid={`notif-toggle-${t.key}`} />
  </li>
);

// ── System Maintenance ───────────────────────────────────────────────────
// Admin-only, collapsed-by-default panel that runs the one-off data
// migrations from the UI (so they never need DevTools again). Every call
// goes through axios → the interceptor attaches the Bearer token, so it
// authenticates even if the session cookie is stale.
const MaintenanceButton = ({ label, method, url, formatResult, testid, icon: Icon }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const res = method === 'GET'
        ? await axios.get(`${BACKEND_URL}${url}`, { withCredentials: true })
        : await axios.post(`${BACKEND_URL}${url}`, {}, { withCredentials: true });
      setResult(res.data);
      toast.success(`${label} complete`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e.message || 'Request failed'));
      toast.error(`${label} failed`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`maint-${testid}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Icon className="h-4 w-4 text-slate-500" />{label}
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={loading} data-testid={`maint-${testid}-btn`}>
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Running…</> : 'Run'}
        </Button>
      </div>
      {error ? <p className="text-xs text-rose-600 mt-2" data-testid={`maint-${testid}-error`}>{error}</p> : null}
      {result ? (
        <div className="mt-2" data-testid={`maint-${testid}-result`}>
          <p className="text-sm font-semibold text-emerald-700">{formatResult(result)}</p>
          <pre className="text-[10px] text-slate-500 bg-slate-50 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
        </div>
      ) : null}
    </div>
  );
};

const DbHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/db-health`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error('Failed to load DB health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const checks = data?.checks || [];
  return (
    <div className="border border-slate-200 rounded-lg p-3 mt-3" data-testid="db-health">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Activity className="h-4 w-4 text-slate-500" /> DB Health
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="db-health-refresh">
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Checking…</> : <><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</>}
        </Button>
      </div>
      {checks.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {checks.map((c) => (
            <li key={c.key} className="flex items-center justify-between py-2 text-sm" data-testid={`db-health-${c.key}`}>
              <span className="text-slate-600">{c.label}</span>
              {c.count === 0 ? (
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 className="h-4 w-4" /> OK</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`db-health-${c.key}-count`}><AlertTriangle className="h-4 w-4" /> {c.count}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-400 py-2">{loading ? 'Checking…' : 'No data'}</p>
      )}
    </div>
  );
};

const fmtDateFound = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// One reviewable discrepancy row (module-level so the visual-edits Babel plugin
// never recurses into a nested member-expression .map() inside JSX).
const AuditLogRow = ({ it, onReviewed }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(it.reviewNotes || '');
  const [saving, setSaving] = useState(false);

  const submit = async (reviewed) => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/revenue-audit/log/${it.logId}/review`, { reviewed, notes }, { withCredentials: true });
      onReviewed(res.data);
      setOpen(false);
      toast.success(reviewed ? 'Marked reviewed' : 'Review cleared');
    } catch {
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const refId = it.invoiceNumber || it.displayTripId || it.tripId || '—';
  return (
    <div className="py-2.5" data-testid={`audit-row-${it.logId}`}>
      <div className="grid grid-cols-12 gap-2 items-center text-xs">
        <span className="col-span-3 font-mono text-slate-700 truncate" title={refId}>{refId}</span>
        <span className="col-span-2 text-right text-slate-600">₹{Number(it.expected ?? 0).toLocaleString('en-IN')}</span>
        <span className="col-span-2 text-right text-slate-600">₹{Number(it.stored ?? 0).toLocaleString('en-IN')}</span>
        <span className="col-span-2 text-right font-semibold text-rose-600">₹{Number(it.difference ?? 0).toLocaleString('en-IN')}</span>
        <span className="col-span-2 text-slate-400">{fmtDateFound(it.createdAt)}</span>
        <span className="col-span-1 text-right">
          {it.reviewed ? (
            <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid={`audit-reviewed-${it.logId}`}><CheckCircle2 className="h-3.5 w-3.5" /></span>
          ) : (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setOpen((o) => !o)} data-testid={`audit-review-btn-${it.logId}`}>Review</Button>
          )}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mt-0.5">{it.type} · {it.detail}</p>
      {it.reviewed && it.reviewNotes ? (
        <p className="text-[11px] text-emerald-700 mt-0.5">Reviewed by {it.reviewedBy}: {it.reviewNotes}</p>
      ) : null}
      {open && !it.reviewed ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Review notes (action decided)…"
            className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5"
            data-testid={`audit-notes-${it.logId}`}
          />
          <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={() => submit(true)} data-testid={`audit-mark-reviewed-${it.logId}`}>
            {saving ? '…' : 'Mark reviewed'}
          </Button>
        </div>
      ) : null}
      {it.reviewed ? (
        <button type="button" className="text-[11px] text-slate-400 hover:text-slate-600 mt-0.5" onClick={() => submit(false)} data-testid={`audit-unreview-${it.logId}`}>Undo review</button>
      ) : null}
    </div>
  );
};

const RevenueAudit = () => {
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [unreviewed, setUnreviewed] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadLog = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/revenue-audit/log`, { withCredentials: true });
      setLog(res.data.items || []);
      setUnreviewed(res.data.unreviewed || 0);
    } catch { /* noop */ }
  };

  useEffect(() => { loadLog(); }, []);

  const runAndLog = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/revenue-audit`, {}, { withCredentials: true });
      setSummary(res.data);
      await loadLog();
      toast.success(res.data.healthy ? 'Books reconcile — no discrepancies' : `${res.data.discrepancyCount} discrepancy(ies) logged`);
    } catch {
      toast.error('Revenue audit failed');
    } finally {
      setLoading(false);
    }
  };

  const onRowReviewed = (updated) => {
    setLog((rows) => rows.map((r) => (r.logId === updated.logId ? updated : r)));
    setUnreviewed((n) => Math.max(0, n - 1));
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 mt-3" data-testid="revenue-audit">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Activity className="h-4 w-4 text-slate-500" /> Revenue Audit
          {log.length > 0 ? (
            <span className="text-[11px] text-slate-400">· {log.length} logged · {unreviewed} to review</span>
          ) : null}
        </div>
        <Button size="sm" variant="outline" onClick={runAndLog} disabled={loading} data-testid="revenue-audit-run" className="border-amber-300 text-amber-700">
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Auditing…</> : <><RefreshCw className="h-3.5 w-3.5 mr-1" />Run &amp; log</>}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">Read-only reconciliation: invoice base+GST+TCS vs stored total, and per-trip invoice sums vs trip value. Records are never auto-fixed — review and decide the action per row.</p>

      {log.length > 0 ? (
        <div data-testid="revenue-audit-log">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100 pb-1">
            <span className="col-span-3">Trip / Invoice ID</span>
            <span className="col-span-2 text-right">Expected</span>
            <span className="col-span-2 text-right">Stored</span>
            <span className="col-span-2 text-right">Difference</span>
            <span className="col-span-2">Date found</span>
            <span className="col-span-1 text-right">Review</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {log.map((it) => (
              <AuditLogRow key={it.logId} it={it} onReviewed={onRowReviewed} />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400 py-2">{summary ? 'No discrepancies — books reconcile.' : 'Run the audit to populate discrepancies.'}</p>
      )}
    </div>
  );
};

const SystemMaintenance = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.role !== 'admin') return null;
  return (
    <Card className="p-6 mb-6 border-slate-200" data-testid="system-maintenance">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between"
        data-testid="system-maintenance-toggle"
      >
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-slate-900">System Maintenance</h2>
          <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Admin only</span>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open ? (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-slate-500">One-off data migrations. All are idempotent — safe to re-run.</p>
          <MaintenanceButton
            label="Run Display ID migration" method="POST" url="/api/admin/migrate/display-ids"
            testid="display-ids" icon={Database}
            formatResult={(r) => `${r.uccBackfilled} clients updated · ${r.tripBackfilled} trips updated · ${r.invoiceBackfilled} invoices`}
          />
          <MaintenanceButton
            label="Normalize ledger trip IDs" method="POST" url="/api/ledger/normalize-trip-ids"
            testid="normalize-ledger" icon={Database}
            formatResult={(r) => `${r?.ledger?.updated ?? 0} entries normalized (scanned ${r?.ledger?.scanned ?? 0}) · ${r?.expenses?.updated ?? 0} expenses`}
          />
          <MaintenanceButton
            label="Run public-refs migration" method="POST" url="/api/admin/migrate/public-refs"
            testid="public-refs" icon={Database}
            formatResult={(r) => `${r?.itineraries?.backfilled ?? 0} itineraries · ${r?.payment_links?.backfilled ?? 0} payment links${r?.complete ? ' · ✓ complete' : ''}`}
          />
          <MaintenanceButton
            label="Display ID status" method="GET" url="/api/admin/migrate/display-ids/status"
            testid="display-status" icon={CheckCircle2}
            formatResult={(r) => `${r?.migrationComplete ? '✓' : '⚠'} ${r?.clients?.withDisplayUcc ?? 0}/${r?.clients?.total ?? 0} clients · ${r?.migrationComplete ? '✓' : '⚠'} ${r?.trips?.withDisplayTripId ?? 0}/${r?.trips?.total ?? 0} trips`}
          />
          <MaintenanceButton
            label="PII encryption status" method="GET" url="/api/admin/migrate/pii-encryption/status"
            testid="pii-status" icon={CheckCircle2}
            formatResult={(r) => `${r?.plaintext_pii_total === 0 ? '✓ all encrypted' : '⚠ ' + r?.plaintext_pii_total + ' plaintext PII values'} · scanned ${r?.scanned ?? 0} clients · passport enc ${r?.top_level?.passportNumber?.encrypted ?? 0}/${(r?.top_level?.passportNumber?.encrypted ?? 0) + (r?.top_level?.passportNumber?.plaintext ?? 0)}`}
          />
          <MaintenanceButton
            label="Run PII encryption migration" method="POST" url="/api/admin/migrate/pii-encryption?apply=true"
            testid="pii-migrate" icon={Database}
            formatResult={(r) => `${r?.updated ?? 0} clients updated · ${r?.plaintext_pii_total ?? 0} plaintext values encrypted this run · blind-index backfilled ${r?.blind_index?.missing_backfilled ?? 0}`}
          />
          <MaintenanceButton
            label="Scan orphan ledger rows (read-only)" method="GET" url="/api/admin/integrity/orphan-ledger"
            testid="orphan-ledger-scan" icon={CheckCircle2}
            formatResult={(r) => `${r?.total === 0 ? '✓ no orphans' : '⚠ ' + r?.total + ' orphan ledger row(s)'} · ${r?.live ?? '?'} live rows intact (of ${r?.scanned ?? '?'} scanned) · trip ${r?.breakdown?.missing_trip ?? 0} · invoice ${r?.breakdown?.missing_invoice ?? 0} · receipt ${r?.breakdown?.missing_receipt ?? 0} (read-only, sample PII-masked)`}
          />
          <MaintenanceButton
            label="Archive & sweep orphan ledger rows" method="POST" url="/api/admin/integrity/orphan-ledger/sweep?confirm=true"
            testid="orphan-ledger-sweep" icon={Database}
            formatResult={(r) => r?.executed ? `Archived ${r?.archived ?? 0} then deleted ${r?.deleted ?? 0} orphan row(s)` : (r?.message || `Dry run — ${r?.total ?? 0} would be swept`)}
          />
          <MaintenanceButton
            label="View archive audit trail (read-only)" method="GET" url="/api/admin/integrity/archive"
            testid="archive-view" icon={Archive}
            formatResult={(r) => `${r?.total ?? 0} archived row(s)${(r?.breakdown || []).length ? ' · ' + r.breakdown.map((b) => `${b.count} ${b.sourceCollection} (${b.reason})`).join(' · ') : ' (nothing archived yet)'}`}
          />
          <DbHealth />
          <RevenueAudit />
        </div>
      ) : null}
    </Card>
  );
};

const Admin = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  return (
    <div data-testid="admin-page">
      <PendingAgents />
      <AdminNotifications />
      <SystemMaintenance />
      {isAdmin && <PiiAccessLog />}
      {/* The full Settings page lives below — Agency Profile (Company
          Details), Branding/Logo, Payment Gateway, and Agents & Users
          all live there. */}
      <Settings />
    </div>
  );
};

export default Admin;
